// The delivery week, planned as a unit. Pure.
//
// Matt, 2026-09-23: "plan like a week's worth of lessons ... on Monday plan
// it, make it so that we have the whole order, figure out what we're making,
// order everything from South Mountain Creamery plus whatever groceries I
// need to pick up, and then have them assigned to days in the regular meal
// hat rotation. Although they don't go into the hat, they just get assigned."
//
// The household's cycle: order by Monday, delivery Wednesday night /
// Thursday morning. So a planning week runs THURSDAY to WEDNESDAY, and the
// week being planned is always the one whose delivery follows the next
// Monday cutoff — on a Tuesday that is not this Thursday (the order has
// already closed) but the one after.
import { todayISO } from './course.js';

const DAY = 86400000;
const addDays = (date, n) => new Date(date.getTime() + n * DAY);

/** The week to plan right now: its Monday cutoff, its seven days (Thu…Wed), and a stable key. */
export function planningWeek (now = new Date()) {
  const day = now.getDay(); // 0 Sun … 6 Sat
  const daysToMonday = (1 - day + 7) % 7;
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + daysToMonday);
  const thursday = addDays(monday, 3);
  const days = Array.from({ length: 7 }, (_, i) => todayISO(addDays(thursday, i)));
  return { orderBy: todayISO(monday), arrives: days[0], days, key: days[0] };
}

const isWeekend = (iso) => { const d = new Date(`${iso}T12:00:00`).getDay(); return d === 0 || d === 6; };

/**
 * Spread N lessons over the week. Weekend lessons (the braise) take Saturday
 * or Sunday first; the rest go on the earliest free days, leaving a gap
 * between lessons where the count allows so it never becomes five nights in
 * a row of homework.
 */
export function defaultAssignment (lessons, days) {
  const free = [...days];
  const assignment = {};
  const take = (iso) => { free.splice(free.indexOf(iso), 1); return iso; };

  for (const lesson of lessons.filter((l) => l.weekend)) {
    const weekend = free.find(isWeekend);
    if (weekend) assignment[lesson.id] = take(weekend);
  }
  const rest = lessons.filter((l) => !assignment[l.id]);
  // Even spacing: with 2 lessons over 7 days, Thu and Sun; with 3, Thu/Sat/Mon.
  const stride = rest.length ? Math.max(1, Math.floor(free.length / rest.length)) : 1;
  let cursor = 0;
  for (const lesson of rest) {
    const iso = free[Math.min(cursor, free.length - 1)];
    if (!iso) break;
    assignment[lesson.id] = iso;
    free.splice(free.indexOf(iso), 1);
    cursor += stride - 1;
  }
  return assignment;
}

/** One creamery list for the week: the same cut ordered by two lessons shows once, with both quantities. */
export function aggregateOrder (lessons) {
  const byId = new Map();
  for (const lesson of lessons) {
    for (const item of lesson.order || []) {
      const entry = byId.get(item.creameryId) || { creameryId: item.creameryId, name: item.name, lines: [], notes: [] };
      entry.lines.push({ lessonNumber: lesson.number, lessonTitle: lesson.title, qty: item.qty });
      if (item.note) entry.notes.push(item.note);
      byId.set(item.creameryId, entry);
    }
  }
  return [...byId.values()];
}

/**
 * One grocery list for the week: ingredients merged by name (aliases pooled),
 * `pantry` only if every lesson calling for it treats it as a staple, and the
 * lessons that want it listed so the screen can say why.
 */
export function aggregateGrocery (lessons) {
  const byName = new Map();
  for (const lesson of lessons) {
    for (const ing of lesson.grocery || []) {
      const key = String(ing.name).trim().toLowerCase();
      const entry = byName.get(key) || { name: ing.name, aliases: [], pantry: true, qtys: [], lessons: [] };
      entry.aliases = Array.from(new Set([...entry.aliases, ...(ing.aliases || [])]));
      entry.pantry = entry.pantry && Boolean(ing.pantry);
      if (ing.qty) entry.qtys.push(`${ing.qty} (L${lesson.number})`);
      entry.lessons.push(lesson.number);
      byName.set(key, entry);
    }
  }
  return [...byName.values()].map((e) => ({ ...e, qty: e.qtys.join(', ') }));
}

export const dayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
export const weekdayLabel = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'long' });
